import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { BedDouble, Bell, BellOff, Check, CheckCircle2, ChevronDown, Coins, Copy, Eye, FileDown, ListChecks, Loader2, Mail, Pencil, Plus, Receipt, Send, Trash2, UserPlus, Users, X } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { evalMoneyExpr, fmtIsk, fmtNum2, fmtUsdApprox, ISK_PER_USD, sumIsk, moneyToIsk, nameKey, hotelPriceForTotal } from "@/lib/price";
import TripBuilder from "./TripBuilder";
import { TripCalendar } from "./TripCalendar";
import { TripView } from "./TripView";
import { AgentSheet } from "./AgentSheet";

// One-line money summary for a trip card: bill, cost and profit (the owner's commission —
// entered commission wins, otherwise price − cost). Stay-linked itinerary lines are skipped:
// they mirror their stay, so the stay alone carries the money. Same rules as the Money panel.
const profitFor = (t) => {
  const isMirrored = (i) => {
    if (i.type !== "hotel") return false;
    const tt = nameKey(i.title);
    return (t.hotels || []).some((h) => {
      const hn = nameKey(h.name);
      if (!hn) return false;
      const first = hn.split(/\s+/)[0];
      return tt.includes(hn) || (first.length > 3 && tt.includes(first));
    });
  };
  const rows = [
    ...(t.hotels || []).map((h) => ({ ...h, price: hotelPriceForTotal(h) })),
    ...(t.items || []).filter((i) => !isMirrored(i)),
    ...(t.rentals || []),
  ];
  let bill = 0, cost = 0, profit = 0;
  rows.forEach((r) => {
    const p = moneyToIsk(r.price) || 0;
    const c = moneyToIsk(r.cost) || 0;
    bill += p;
    cost += c;
    profit += moneyToIsk(r.commission) || 0; // commission = the commission boxes only; empty counts as 0
  });
  return { bill, cost, profit };
};

const TRIP_STATUS_META = {
  draft: { label: "Draft", cls: "border-neutral-600 bg-neutral-800/40 text-neutral-400" },
  sent_to_client: { label: "Awaiting Client", cls: "border-sky-400/50 bg-sky-500/10 text-sky-200" },
  changes_requested: { label: "Adjustments Requested", cls: "border-orange-400/50 bg-orange-500/10 text-orange-200" },
  pending_confirmation: { label: "Pending Confirmation", cls: "border-amber-400/60 bg-amber-500/10 text-amber-200" },
  sent_to_operator: { label: "With Operator", cls: "border-violet-400/50 bg-violet-500/10 text-violet-200" },
  confirmed: { label: "Confirmed", cls: "border-emerald-400/50 bg-emerald-500/10 text-emerald-200" },
};

const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return d;
  }
};

// Trips arriving within 14 days with rows still not confirmed with operators
const REMINDER_DAYS = 14;
// fully-booked (needs an alternative, not a booking), person-books-it and just-info rows need no operator follow-up
const needsBooking = (r) => !r.ops_confirmed && !r.ops_full && !r.ops_client_books && !r.ops_person && !r.ops_info;
const unconfirmedCount = (t) => [...(t.hotels || []), ...(t.items || [])].filter(needsBooking).length;
const bookingTotal = (t) => [...(t.hotels || []), ...(t.items || [])].filter((r) => !r.ops_full && !r.ops_client_books && !r.ops_person && !r.ops_info).length;
const daysToArrival = (t) => {
  if (!t.arrival_date) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(`${t.arrival_date}T00:00:00`) - today) / 86400000);
};
const reminderFor = (t) => {
  if (t.reminders_off) return null; // muted by the owner — e.g. not a paying customer
  const days = daysToArrival(t);
  const open = unconfirmedCount(t);
  if (days === null || days < 0 || days > REMINDER_DAYS || open === 0) return null;
  return { days, open, total: bookingTotal(t), urgent: days <= 7 };
};

// jobs with money on them that have no vendor invoice attached and aren't marked "myself"
// (auto check-in/out moments excluded — the stay row carries the invoice; rentals included).
// This one is about the owner's own VAT credit, so reminders_off does NOT mute it.
const vendorMissing = (t) =>
  [...(t.hotels || []), ...(t.items || []), ...(t.rentals || [])].filter(
    (r) =>
      r.type !== "hotel" &&
      !r.ops_full &&
      !r.ops_info &&
      !r.ops_person &&
      !r.ops_client_books &&
      !r.self_done &&
      !r.no_invoice &&
      (String(r.price || "").trim() || String(r.cost || "").trim()) &&
      !r.vendor_invoice_path
  );
const vendorReminderFor = (t) => {
  const days = daysToArrival(t);
  if (days === null || days < 0 || days > REMINDER_DAYS) return null;
  const missing = vendorMissing(t);
  return missing.length ? { days, missing } : null;
};

// soonest upcoming cancellation deadline across stays + rentals (past ones are gone — nothing to show)
const nextCancelDeadline = (t) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayStr = today.toISOString().slice(0, 10);
  const all = [
    ...(t.hotels || []).filter((h) => h.cancel_by && !h.ops_full).map((h) => ({ d: h.cancel_by, label: h.name })),
    ...(t.rentals || []).filter((r) => r.cancel_by).map((r) => ({ d: r.cancel_by, label: r.name })),
  ]
    .filter((x) => x.d >= todayStr)
    .sort((a, b) => a.d.localeCompare(b.d));
  if (!all.length) return null;
  const days = Math.round((new Date(`${all[0].d}T00:00:00`) - today) / 86400000);
  return { ...all[0], days, count: all.length };
};

// the client you were last working on stays pinned on top; the rest are ordered by
// the next upcoming trip first, then most recent past. the pin is remembered per device
// (localStorage) with the backend's updated_at as the cross-device fallback.
const LAST_TRIP_KEY = "er-last-trip";
const sortTrips = (ts, lastId) => {
  const at = (t) => (t.arrival_date ? new Date(`${t.arrival_date}T00:00:00`).getTime() : Number.POSITIVE_INFINITY);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const upcoming = ts.filter((t) => at(t) >= now.getTime()).sort((a, b) => at(a) - at(b));
  const past = ts.filter((t) => at(t) < now.getTime()).sort((a, b) => at(b) - at(a));
  const ordered = [...upcoming, ...past];
  if (!ordered.length) return ordered;
  const latest = ordered.reduce((m, t) => ((t.updated_at || "") > (m.updated_at || "") ? t : m), ordered[0]);
  const pinned = ordered.find((t) => t.id === lastId) || latest;
  return [pinned, ...ordered.filter((t) => t.id !== pinned.id)];
};

export default function TripsView({ token, openNewOnMount = false }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [trips, setTrips] = useState(null);
  const [editing, setEditing] = useState();
  const [previewTrip, setPreviewTrip] = useState(null);
  const [busy, setBusy] = useState("");
  const [seenAt] = useState(() => localStorage.getItem("er-wishes-seen") || "");
  const [roomingFor, setRoomingFor] = useState(null);
  const [roomingEmail, setRoomingEmail] = useState("");
  const [demoFor, setDemoFor] = useState(null);
  const [demoEmail, setDemoEmail] = useState("");
  const [adminFor, setAdminFor] = useState(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [sheetTrip, setSheetTrip] = useState(null);
  const [lastTripId, setLastTripId] = useState(() => localStorage.getItem(LAST_TRIP_KEY) || "");
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // opening a client's builder marks them as "last worked on" — they stay pinned on top
  const openEditor = (t) => {
    localStorage.setItem(LAST_TRIP_KEY, t.id);
    setLastTripId(t.id);
    setSwitcherOpen(false);
    setEditing(t);
  };
  const [costsFor, setCostsFor] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [openCostsNext, setOpenCostsNext] = useState(false); // which card shows its options — tap the client name
  const [wishesFor, setWishesFor] = useState(null); // trip whose wishes modal is open
  const [newRequests, setNewRequests] = useState(null); // new client requests popup on sign-in

  // after sign-in the owner lands here — surface fresh requests as a popup (once per session)
  useEffect(() => {
    axios
      .get(`${API}/owner/requests`, { headers })
      .then((r) => {
        const fresh = (r.data.requests || []).filter((q) => q.status === "new");
        if (fresh.length && !sessionStorage.getItem("er-requests-pop")) {
          setNewRequests(fresh);
          sessionStorage.setItem("er-requests-pop", "1");
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // one tap on a request opens the builder pre-filled with the client's details
  const buildFromRequest = (q) => {
    setNewRequests(null);
    setEditing({
      client: { name: q.name || "", email: q.email || "", phone: q.phone || "" },
      arrival_date: q.arrival_date || "",
      departure_date: q.departure_date || "",
      pax: q.party_size || "2",
      notes: `${q.message || ""}${(q.services || []).length ? `\n\nRequested: ${q.services.join(", ")}` : ""}`.trim(),
    });
  };

  const markWishFixed = async (t, index) => {
    try {
      await axios.post(`${API}/owner/trips/${t.id}/wish-handled`, { index }, { headers });
      const wishes = (t.client_wishes || []).map((w, i) => (i === index ? { ...w, handled: true } : w));
      const updated = { ...t, client_wishes: wishes };
      setTrips((ts) => ts.map((x) => (x.id === t.id ? updated : x)));
      setWishesFor(updated);
      toast.success("Marked as fixed");
    } catch {
      toast.error("Could not update the wish.");
    }
  };
  const [costs, setCosts] = useState(null); // { costs, total_isk, open_isk }
  const [vatSummary, setVatSummary] = useState(null);
  const [costAmount, setCostAmount] = useState("");
  const [costNote, setCostNote] = useState("");
  const [costBusy, setCostBusy] = useState(false);
  const [photoUrls, setPhotoUrls] = useState({}); // cost_id -> object URL
  const [photoZoom, setPhotoZoom] = useState(null);
  const costPhotoRef = useRef(null);

  const fetchCostPhoto = async (tripId, costId) => {
    try {
      const res = await axios.get(`${API}/owner/cost-photo/${tripId}/${costId}`, { headers, responseType: "blob" });
      setPhotoUrls((m) => (m[costId] ? m : { ...m, [costId]: URL.createObjectURL(res.data) }));
    } catch {
      /* photo unavailable */
    }
  };

  const loadCosts = async (tripId) => {
    try {
      const res = await axios.get(`${API}/owner/trips/${tripId}/costs`, { headers });
      setCosts(res.data);
      patchLocal(tripId, { extra_costs: res.data.costs });
      res.data.costs.filter((c) => c.photo_path).forEach((c) => fetchCostPhoto(tripId, c.cost_id));
    } catch {
      setCosts({ costs: [], total_isk: 0, open_isk: 0 });
      toast.error("Could not load the costs.");
    }
    try {
      const vat = await axios.get(`${API}/owner/trips/${tripId}/vat-summary`, { headers });
      setVatSummary(vat.data);
    } catch {
      setVatSummary(null);
    }
  };

  const openCosts = (t) => {
    setCostsFor(t);
    setCosts(null);
    setVatSummary(null);
    setCostAmount("");
    setCostNote("");
    if (costPhotoRef.current) costPhotoRef.current.value = "";
    loadCosts(t.id);
  };

  const addCost = async () => {
    const file = costPhotoRef.current?.files?.[0] || null;
    if (!costAmount.trim() && !costNote.trim() && !file) {
      toast.error("Write an amount, a note, or attach a photo.");
      return;
    }
    setCostBusy(true);
    try {
      const res = await axios.post(
        `${API}/owner/trips/${costsFor.id}/costs`,
        { amount: costAmount, note: costNote.trim() || (file ? "Receipt photo" : "") },
        { headers }
      );
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/owner/trips/${costsFor.id}/costs/${res.data.cost_id}/photo`, fd, { headers });
      }
      toast.success("Cost logged — only you see it");
      setCostAmount("");
      setCostNote("");
      if (costPhotoRef.current) costPhotoRef.current.value = "";
      await loadCosts(costsFor.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not log the cost.");
    } finally {
      setCostBusy(false);
    }
  };

  const toggleCostBilled = async (c) => {
    try {
      await axios.post(`${API}/owner/trips/${costsFor.id}/costs/${c.cost_id}/billed`, { billed: !c.billed }, { headers });
      loadCosts(costsFor.id);
    } catch {
      toast.error("Could not update it.");
    }
  };

  const deleteCost = async (c) => {
    try {
      await axios.delete(`${API}/owner/trips/${costsFor.id}/costs/${c.cost_id}`, { headers });
      toast.success("Cost removed");
      loadCosts(costsFor.id);
    } catch {
      toast.error("Could not remove it.");
    }
  };

  const toggleReminders = async (t) => {
    const off = !t.reminders_off;
    try {
      await axios.post(`${API}/owner/trips/${t.id}/reminders-toggle`, { off }, { headers });
      patchLocal(t.id, { reminders_off: off });
      toast.success(off ? `Reminders off for ${t.client?.name}` : `Reminders back on for ${t.client?.name}`);
    } catch {
      toast.error("Could not change that right now.");
    }
  };

  const savePreviewItems = async (items) => {
    await axios.put(
      `${API}/owner/trips/${previewTrip.id}`,
      {
        client: previewTrip.client,
        client_region: previewTrip.client_region || "world",
        arrival_date: previewTrip.arrival_date,
        departure_date: previewTrip.departure_date,
        arrival_time: previewTrip.arrival_time || "",
        departure_time: previewTrip.departure_time || "",
        location: previewTrip.location || "",
        pax: previewTrip.pax,
        hotels: previewTrip.hotels || [],
        items,
        deposit_amount: previewTrip.deposit_amount || 0,
        deposit_currency: previewTrip.deposit_currency || "usd",
        deposit_link: previewTrip.deposit_link || "",
        deposit_instructions: previewTrip.deposit_instructions || "",
        is_public_tour: previewTrip.is_public_tour || false,
        ticket_price: previewTrip.ticket_price || "",
        capacity: previewTrip.capacity || 0,
        hide_spots: previewTrip.hide_spots || false,
      },
      { headers }
    );
    patchLocal(previewTrip.id, { items });
    setPreviewTrip((t) => ({ ...t, items }));
  };

  useEffect(() => {
    localStorage.setItem("er-wishes-seen", new Date().toISOString());
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/owner/trips`, { headers: { Authorization: `Bearer ${token}` } });
      setTrips(res.data.trips);
    } catch {
      toast.error("Could not load itineraries.");
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  // the mobile hub's "New booking" drops straight into a blank itinerary
  useEffect(() => {
    if (openNewOnMount) setEditing(null);
  }, [openNewOnMount]);

  const sortedTrips = trips ? sortTrips(trips, lastTripId) : null;
  const pinnedId = sortedTrips && sortedTrips.length > 1 ? sortedTrips[0].id : null;

  // finished journeys: departure day passed (auto) or marked done by hand. The money question decides
  // whether they leave the books (settled -> Former clients) or stay red until paid (owes).
  const [tripsTab, setTripsTab] = useState("active");
  const isFormer = (t) => !!t.done_at && t.settlement === "settled";
  const owesMoney = (t) => !!t.done_at && t.settlement === "owes";
  const settlementPending = (t) => !!t.done_at && (!t.settlement || t.settlement === "pending");
  const visibleTrips = sortedTrips ? sortedTrips.filter((t) => (tripsTab === "former" ? isFormer(t) : !isFormer(t))) : null;

  const markDone = async (t, done) => {
    setBusy(`done:${t.id}`);
    try {
      await axios.post(`${API}/owner/trips/${t.id}/done`, { done }, { headers });
      toast.success(done ? `${t.client.name} marked finished` : `${t.client.name} back on the books`);
      await load();
    } catch {
      toast.error("Could not update that right now");
    } finally {
      setBusy("");
    }
  };

  const settle = async (t, settlement) => {
    setBusy(`settle:${t.id}`);
    try {
      await axios.post(`${API}/owner/trips/${t.id}/settlement`, { settlement }, { headers });
      toast.success(
        settlement === "owes"
          ? `${t.client.name} flagged as owing — you'll get a daily reminder until it's paid`
          : `${t.client.name} settled — moved to Former clients`
      );
      await load();
    } catch {
      toast.error("Could not update that right now");
    } finally {
      setBusy("");
    }
  };

  const patchLocal = (id, patch) => setTrips((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  const sendToClient = async (t) => {
    setBusy(t.id);
    try {
      const res = await axios.post(`${API}/owner/trips/${t.id}/send`, {}, { headers });
      patchLocal(t.id, { status: "sent_to_client" });
      toast.success(
        res.data.email_id
          ? `Emailed to ${t.client.email} — private link is live`
          : "Marked as sent — share the private link with your client"
      );
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send to client.");
    } finally {
      setBusy("");
    }
  };

  const lifecycle = async (t, action, newStatus, label) => {
    setBusy(t.id);
    try {
      await axios.post(`${API}/owner/trips/${t.id}/${action}`, {}, { headers });
      patchLocal(t.id, { status: newStatus });
      toast.success(label);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not update the trip.");
    } finally {
      setBusy("");
    }
  };

  const remove = async (t) => {
    setBusy(t.id);
    try {
      await axios.delete(`${API}/owner/trips/${t.id}`, { headers });
      setTrips((ts) => ts.filter((x) => x.id !== t.id));
      toast.success("Itinerary deleted");
    } catch {
      toast.error("Could not delete the itinerary.");
    } finally {
      setBusy("");
    }
  };

  if (editing !== undefined) {
    return (
      <TripBuilder
        token={token}
        trip={editing}
        openCostsOnMount={openCostsNext}
        onDone={() => {
          setEditing(undefined);
          setOpenCostsNext(false);
          load();
        }}
        onCancel={() => {
          setEditing(undefined);
          setOpenCostsNext(false);
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-400">
          {trips === null ? "Loading..." : `${trips.length} client ${trips.length === 1 ? "itinerary" : "itineraries"}`}
          {trips !== null &&
            (() => {
              const fresh = trips.reduce(
                (n, t) => n + (t.client_wishes || []).filter((w) => !seenAt || w.at > seenAt).length,
                0
              );
              return fresh > 0 ? (
                <span data-testid="new-wishes-badge" className="ml-3 animate-pulse border border-amber-400/60 bg-amber-500/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200">
                  {fresh} new {fresh === 1 ? "wish" : "wishes"}
                </span>
              ) : null;
            })()}
        </p>
        <div className="flex items-center gap-2">
          {trips !== null && trips.length > 0 && (
            <div className="relative">
              <button
                data-testid="client-switcher-button"
                onClick={() => setSwitcherOpen((o) => !o)}
                aria-expanded={switcherOpen}
                className="flex items-center gap-2 border border-amber-500/50 bg-amber-500/10 px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
              >
                <Users className="h-3.5 w-3.5" /> All Clients <ChevronDown className={`h-3 w-3 transition-transform ${switcherOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {switcherOpen && (
                  <>
                    <div data-testid="client-switcher-backdrop" className="fixed inset-0 z-40" onClick={() => setSwitcherOpen(false)} />
                    <motion.div
                    data-testid="client-switcher-panel"
                    className="absolute right-0 top-full z-50 mt-2 w-80 max-w-[85vw] border border-amber-500/25 bg-[#0c0c10] shadow-2xl shadow-black/60"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                  >
                    <p className="border-b border-amber-500/15 px-4 py-3 text-[9px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">
                      Jump straight into a client
                    </p>
                    <div className="max-h-72 overflow-y-auto">
                      {sortedTrips.map((t) => {
                        const meta = TRIP_STATUS_META[t.status] || TRIP_STATUS_META.draft;
                        return (
                          <button
                            key={t.id}
                            data-testid={`client-switcher-item-${t.id.slice(0, 6)}`}
                            onClick={() => openEditor(t)}
                            className={`flex w-full items-center justify-between gap-3 border-b border-neutral-800/60 px-4 py-3 text-left transition-colors hover:bg-amber-500/10 ${t.id === pinnedId ? "bg-amber-500/5" : ""}`}
                          >
                            <span className="min-w-0">
                              <span className="block truncate font-serif text-sm text-neutral-100">{t.client.name}</span>
                              <span className="mt-0.5 block text-[9px] uppercase tracking-[0.18em] text-neutral-500">
                                {fmtDate(t.arrival_date)} → {fmtDate(t.departure_date)}
                                {t.id === pinnedId ? " · last worked on" : ""}
                              </span>
                            </span>
                            <span className={`shrink-0 border px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.15em] ${meta.cls}`}>{meta.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          )}
          <button
            data-testid="trip-new-button"
            onClick={() => setEditing(null)}
            className="flex items-center gap-2 bg-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300"
          >
            <Plus className="h-4 w-4" /> New Itinerary
          </button>
        </div>
      </div>

      {trips !== null &&
        (() => {
          const flagged = trips.map((t) => ({ t, r: reminderFor(t) })).filter((x) => x.r);
          if (!flagged.length) return null;
          return (
            <div data-testid="unconfirmed-banner" className="mt-5 border border-red-500/40 bg-red-950/20 px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-300">
                Booking reminders — {flagged.length} {flagged.length === 1 ? "journey needs" : "journeys need"} operator follow-up
              </p>
              <div className="mt-2 space-y-1">
                {flagged.map(({ t, r }) => (
                  <p key={t.id} data-testid={`reminder-row-${t.id.slice(0, 6)}`} className="flex items-center justify-between gap-3 text-xs tracking-wide text-red-200/80">
                    <span>
                      {t.client.name} — arrives {fmtDate(t.arrival_date)} ({r.days === 0 ? "today" : `in ${r.days} day${r.days === 1 ? "" : "s"}`}) ·{" "}
                      {r.open} of {r.total} still not confirmed
                    </span>
                    <button
                      data-testid={`reminder-mute-${t.id.slice(0, 6)}`}
                      onClick={() => toggleReminders(t)}
                      title="Turn off reminders for this client"
                      className="flex shrink-0 items-center gap-1 border border-red-500/30 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-red-200/60 transition-colors hover:border-red-300 hover:text-red-100"
                    >
                      <BellOff className="h-3 w-3" /> Mute
                    </button>
                  </p>
                ))}
              </div>
            </div>
          );
        })()}

      {trips !== null &&
        (() => {
          const flagged = trips.map((t) => ({ t, r: vendorReminderFor(t) })).filter((x) => x.r);
          if (!flagged.length) return null;
          return (
            <div data-testid="vendor-invoice-banner" className="mt-4 border border-amber-500/40 bg-amber-950/20 px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300">
                Vendor invoices — {flagged.length} {flagged.length === 1 ? "journey arrives" : "journeys arrive"} soon with no invoice attached
              </p>
              <div className="mt-2 space-y-1">
                {flagged.map(({ t, r }) => (
                  <p key={t.id} data-testid={`vendor-missing-row-${t.id.slice(0, 6)}`} className="flex items-center justify-between gap-3 text-xs leading-relaxed tracking-wide text-amber-200/80">
                    <span>
                      {t.client.name} — arrives {fmtDate(t.arrival_date)} ({r.days === 0 ? "today" : `in ${r.days} day${r.days === 1 ? "" : "s"}`}) · {r.missing.length} missing:{" "}
                      {r.missing.map((m) => m.title || m.name).join(", ")}
                    </span>
                  </p>
                ))}
              </div>
            </div>
          );
        })()}

      {trips !== null && (
        <>
          {sortedTrips.filter(owesMoney).length > 0 && (
            <div data-testid="debt-banner" className="mt-4 border border-red-500/50 bg-red-950/25 px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-300">
                Still owed — {sortedTrips.filter(owesMoney).length} finished {sortedTrips.filter(owesMoney).length === 1 ? "client hasn't" : "clients haven't"} paid
              </p>
              <div className="mt-2 space-y-1.5">
                {sortedTrips.filter(owesMoney).map((t) => (
                  <p key={t.id} data-testid={`debt-row-${t.id.slice(0, 6)}`} className="flex flex-wrap items-center justify-between gap-3 text-xs leading-relaxed tracking-wide text-red-200/90">
                    <span>
                      {t.client.name} — trip ended {fmtDate(t.departure_date)}
                    </span>
                    <button
                      data-testid={`debt-paid-${t.id.slice(0, 6)}`}
                      disabled={busy !== ""}
                      onClick={() => settle(t, "settled")}
                      className="border border-emerald-500/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black disabled:opacity-40"
                    >
                      Mark paid
                    </button>
                  </p>
                ))}
              </div>
            </div>
          )}
          {sortedTrips.filter(settlementPending).length > 0 && (
            <div data-testid="settlement-banner" className="mt-4 border border-amber-500/40 bg-amber-950/20 px-5 py-4">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300">
                {sortedTrips.filter(settlementPending).length === 1 ? "A trip has finished" : "Trips have finished"} — does the client still owe you money?
              </p>
              <div className="mt-2 space-y-1.5">
                {sortedTrips.filter(settlementPending).map((t) => (
                  <p key={t.id} data-testid={`settlement-row-${t.id.slice(0, 6)}`} className="flex flex-wrap items-center justify-between gap-3 text-xs leading-relaxed tracking-wide text-amber-200/80">
                    <span>
                      {t.client.name} — {fmtDate(t.arrival_date)} → {fmtDate(t.departure_date)}
                    </span>
                    <span className="flex gap-2">
                      <button
                        data-testid={`settlement-owes-${t.id.slice(0, 6)}`}
                        disabled={busy !== ""}
                        onClick={() => settle(t, "owes")}
                        className="border border-red-500/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-red-200 transition-colors hover:bg-red-500 hover:text-black disabled:opacity-40"
                      >
                        Yes — owes money
                      </button>
                      <button
                        data-testid={`settlement-clear-${t.id.slice(0, 6)}`}
                        disabled={busy !== ""}
                        onClick={() => settle(t, "settled")}
                        className="border border-emerald-500/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black disabled:opacity-40"
                      >
                        No — all settled
                      </button>
                    </span>
                  </p>
                ))}
              </div>
            </div>
          )}
          <div className="mt-6 flex gap-2" data-testid="trips-tabs">
            <button
              data-testid="trips-tab-active"
              onClick={() => setTripsTab("active")}
              className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                tripsTab === "active" ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-neutral-700 text-neutral-500 hover:border-amber-400/60 hover:text-amber-200"
              }`}
            >
              On the books ({sortedTrips.filter((t) => !isFormer(t)).length})
            </button>
            <button
              data-testid="trips-tab-former"
              onClick={() => setTripsTab("former")}
              className={`border px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                tripsTab === "former" ? "border-amber-400 bg-amber-500/15 text-amber-200" : "border-neutral-700 text-neutral-500 hover:border-amber-400/60 hover:text-amber-200"
              }`}
            >
              Former clients ({sortedTrips.filter(isFormer).length})
            </button>
          </div>
        </>
      )}

      <div className="mt-8 space-y-10">
        {trips === null ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        ) : trips.length === 0 ? (
          <div data-testid="trips-empty-state" className="border border-dashed border-neutral-700 py-20 text-center">
            <p className="font-serif text-2xl font-light text-neutral-400">No client itineraries yet.</p>
            <p className="mt-2 text-sm text-neutral-600">Compose one and send it for review — nothing confirms itself.</p>
          </div>
        ) : (
          (() => {
          const renderTripCard = (t) => {
            const meta = TRIP_STATUS_META[t.status] || TRIP_STATUS_META.draft;
            const changes = (t.items || []).filter((i) => i.client_action).length;
            const guestPax = (t.guests || []).filter((g) => g.registered).reduce((s, g) => s + (parseInt(g.pax, 10) || 0), 0);
            const headcount = (parseInt(t.pax, 10) || 0) + guestPax;
            const link = `${window.location.origin}/trip/${t.client_token}`;
            const expanded = (expandedId ?? pinnedId) === t.id; // tap the client name to show a card's options
            return (
              <motion.div
                key={t.id}
                layout
                data-testid={`trip-card-${t.id.slice(0, 6)}`}
                className="border border-amber-500/15 bg-[#14141a] p-6"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <button
                      data-testid={`trip-expand-${t.id.slice(0, 6)}`}
                      onClick={() => setExpandedId((expandedId ?? pinnedId) === t.id ? null : t.id)}
                      title="Tap the name to show this client's options"
                      className="text-left font-serif text-3xl font-light text-neutral-50 transition-colors hover:text-amber-200"
                    >
                      {t.client.name}
                      {t.id === pinnedId && (
                        <span data-testid={`trip-pinned-${t.id.slice(0, 6)}`} className="ml-3 border border-amber-400/50 bg-amber-500/10 px-2 py-0.5 align-middle text-[8px] font-semibold uppercase tracking-[0.2em] text-amber-300">
                          Last worked on
                        </span>
                      )}
                      <ChevronDown className={`ml-2 inline h-4 w-4 text-neutral-500 transition-transform ${(expandedId ?? pinnedId) === t.id ? "rotate-180" : ""}`} />
                    </button>
                    <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                      {fmtDate(t.arrival_date)} → {fmtDate(t.departure_date)} ·{" "}
                      <span data-testid={`trip-headcount-${t.id.slice(0, 6)}`} className="text-amber-200/90">
                        {headcount} total pax{guestPax > 0 ? ` (${t.pax} + ${guestPax} guest${guestPax === 1 ? "" : "s"})` : ""}
                      </span>{" "}
                      · {(t.items || []).length} moments · {(t.hotels || []).length} stays
                    </p>
                    {(() => {
                      const m = profitFor(t);
                      if (!m.bill && !m.profit) return null;
                      return (
                        <p data-testid={`trip-profit-${t.id.slice(0, 6)}`} className="mt-1.5 text-xs uppercase tracking-[0.25em]">
                          <span className="text-neutral-400">Bill {fmtIsk(m.bill)} · Cost {fmtIsk(m.cost)} · </span>
                          <span className={m.profit > 0 ? "font-semibold text-emerald-300" : "text-neutral-400"}>Commission {fmtIsk(m.profit)}</span>
                        </p>
                      );
                    })()}
                    {(() => {
                      const missing = vendorMissing(t);
                      if (!missing.length) return null;
                      return (
                        <p data-testid={`trip-missing-invoices-${t.id.slice(0, 6)}`} className="mt-1 text-[10px] uppercase tracking-[0.25em] text-red-400/90">
                          No invoice ×{missing.length} — VAT credit at risk
                        </p>
                      );
                    })()}
                    {(() => {
                      const c = nextCancelDeadline(t);
                      if (!c) return null;
                      const urgent = c.days <= 7;
                      return (
                        <p
                          data-testid={`trip-cancel-deadline-${t.id.slice(0, 6)}`}
                          title={c.count > 1 ? `${c.count} cancellation deadlines on this trip — this is the nearest` : "Free-cancellation deadline"}
                          className={`mt-1 text-[10px] uppercase tracking-[0.25em] ${urgent ? "font-semibold text-amber-300" : "text-neutral-500"}`}
                        >
                          Cancel by {fmtDate(c.d)} — {c.label} · {c.days === 0 ? "today" : `${c.days}d left`}
                          {c.count > 1 ? ` (+${c.count - 1} more)` : ""}
                        </p>
                      );
                    })()}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span data-testid={`trip-status-${t.id.slice(0, 6)}`} className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${meta.cls}`}>
                      {meta.label}
                    </span>
                    {t.done_at && (
                      <span data-testid={`trip-done-${t.id.slice(0, 6)}`} className="border border-neutral-500/60 bg-neutral-700/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-300">
                        Finished
                      </span>
                    )}
                    {owesMoney(t) && (
                      <span data-testid={`trip-owes-${t.id.slice(0, 6)}`} className="animate-pulse border border-red-400/70 bg-red-500/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-red-200">
                        Owes money
                      </span>
                    )}
                    <button
                      data-testid={t.done_at ? `trip-reopen-${t.id.slice(0, 6)}` : `trip-mark-done-${t.id.slice(0, 6)}`}
                      disabled={busy !== ""}
                      onClick={() => markDone(t, !t.done_at)}
                      title={t.done_at ? "Back on the books — the money question resets" : "The trip is over — mark it finished and answer the money question"}
                      className="border border-neutral-700 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-neutral-500 transition-colors hover:border-amber-400 hover:text-amber-200 disabled:opacity-40"
                    >
                      {t.done_at ? "Reopen" : "Mark finished"}
                    </button>
                    {t.deposit_amount > 0 && (
                      <span data-testid={`trip-deposit-${t.id.slice(0, 6)}`} className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${t.deposit_status === "paid" ? "border-emerald-400/50 bg-emerald-500/10 text-emerald-200" : "border-neutral-600 bg-neutral-800/40 text-neutral-400"}`}>
                        {t.deposit_status === "paid" ? "Deposit Paid" : `Deposit ${fmtNum2(Number(t.deposit_amount))} ${(t.deposit_currency || "usd").toUpperCase()} — Unpaid`}
                      </span>
                    )}
                    {changes > 0 && (
                      <span data-testid={`trip-changes-${t.id.slice(0, 6)}`} className="border border-orange-400/50 bg-orange-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-orange-200">
                        {changes} client {changes === 1 ? "change" : "changes"}
                      </span>
                    )}
                    {(() => {
                      const r = reminderFor(t);
                      if (!r) return null;
                      return (
                        <span
                          data-testid={`trip-unconfirmed-${t.id.slice(0, 6)}`}
                          className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${
                            r.urgent
                              ? "animate-pulse border-red-400/70 bg-red-500/15 text-red-200"
                              : "border-amber-400/60 bg-amber-500/10 text-amber-200"
                          }`}
                        >
                          {r.days === 0 ? "Arrives today" : `Arrives in ${r.days}d`} — {r.open} unconfirmed
                        </span>
                      );
                    })()}
                    {(t.client_wishes || []).length > 0 && (() => {
                      const wishes = t.client_wishes || [];
                      const open = wishes.filter((w) => !w.handled).length;
                      return (
                        <button
                          data-testid={`trip-wishes-${t.id.slice(0, 6)}`}
                          onClick={() => setWishesFor(t)}
                          title="Tap to read the client's wish and mark it fixed"
                          className={`border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] transition-colors ${
                            open > 0
                              ? "animate-pulse border-amber-400 bg-amber-500/20 text-amber-100 hover:bg-amber-500 hover:text-black"
                              : "border-neutral-700 bg-transparent text-neutral-500 hover:border-amber-400 hover:text-amber-200"
                          }`}
                        >
                          {open > 0 ? `${open} wish${open > 1 ? "es" : ""} to fix` : "Wishes ✓ all fixed"}
                        </button>
                      );
                    })()}
                  </div>
                </div>

                {expanded && (
                <>
                <p className="mt-3 text-sm text-neutral-400">
                  {t.client.email}
                  {t.client.phone ? ` · ${t.client.phone}` : ""}
                </p>
                <div className="mt-4 flex flex-col gap-2 border-t border-amber-500/10 pt-4 sm:flex-row sm:items-center">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-500">Client link</p>
                  <div className="flex flex-1 items-center gap-2">
                    <input
                      readOnly
                      data-testid={`trip-link-${t.id.slice(0, 6)}`}
                      value={link}
                      onFocus={(e) => e.target.select()}
                      className="w-full border border-amber-900/40 bg-[#0d0d12] px-3 py-2 text-xs text-amber-100 outline-none"
                    />
                    <button
                      data-testid={`trip-copy-link-${t.id.slice(0, 6)}`}
                      onClick={() => {
                        navigator.clipboard.writeText(link);
                        toast.success("Client link copied");
                      }}
                      className="shrink-0 border border-neutral-700 p-2 text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                      aria-label="Copy client link"
                    >
                      <Copy className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-amber-500/10 pt-4">
                  <button
                    data-testid={`trip-edit-${t.id.slice(0, 6)}`}
                    onClick={() => openEditor(t)}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button
                    data-testid={`trip-costs-${t.id.slice(0, 6)}`}
                    title="Every price, cost and commission on one page — staff costs included or not, your call"
                    onClick={() => {
                      setOpenCostsNext(true);
                      setEditing(t);
                    }}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Coins className="h-3 w-3" /> Costs
                  </button>
                  <button
                    data-testid={`trip-preview-${t.id.slice(0, 6)}`}
                    onClick={() => setPreviewTrip(t)}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Eye className="h-3 w-3" /> Demo
                  </button>
                  <button
                    data-testid={`trip-sheet-${t.id.slice(0, 6)}`}
                    onClick={() => setSheetTrip(t)}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <ListChecks className="h-3 w-3" /> Sheet
                  </button>
                  <Link
                    to={`/owner/invoices?trip=${t.id}`}
                    data-testid={`trip-invoice-${t.id.slice(0, 6)}`}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Receipt className="h-3 w-3" /> Invoice
                  </Link>
                  <button
                    data-testid={`trip-receipts-${t.id.slice(0, 6)}`}
                    onClick={() => openCosts(t)}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <Receipt className="h-3 w-3" /> Receipts
                    {(t.extra_costs || []).some((c) => !c.billed) && (
                      <span data-testid={`trip-open-costs-${t.id.slice(0, 6)}`} className="ml-1 rounded-full bg-amber-500 px-1.5 text-[9px] font-bold text-black">
                        {(t.extra_costs || []).filter((c) => !c.billed).length}
                      </span>
                    )}
                  </button>
                  <button
                    data-testid={`trip-reminders-toggle-${t.id.slice(0, 6)}`}
                    onClick={() => toggleReminders(t)}
                    aria-pressed={!!t.reminders_off}
                    title={t.reminders_off ? "Booking reminders are OFF for this client — tap to turn back on" : "Booking reminders on — tap to mute this client"}
                    className={`flex items-center gap-1.5 border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] transition-colors ${
                      t.reminders_off
                        ? "border-neutral-700 text-neutral-600 hover:border-amber-400 hover:text-amber-200"
                        : "border-neutral-700 text-neutral-300 hover:border-amber-400 hover:text-amber-200"
                    }`}
                  >
                    {t.reminders_off ? <BellOff className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                    {t.reminders_off ? "Muted" : "Remind"}
                  </button>
                  <button
                    data-testid={`trip-pdf-${t.id.slice(0, 6)}`}
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/owner/trips/${t.id}/pdf`, { headers, responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `exclusive-reykjavik-${t.client.name.replace(/\s+/g, "-").toLowerCase()}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        toast.error("Could not generate the PDF.");
                      }
                    }}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <FileDown className="h-3 w-3" /> PDF
                  </button>
                  <button
                    data-testid={`trip-calendar-${t.id.slice(0, 6)}`}
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/owner/trips/${t.id}/calendar`, { headers, responseType: "blob" });
                        const url = URL.createObjectURL(new Blob([res.data], { type: "text/calendar" }));
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `exclusive-reykjavik-${t.client.name.replace(/\s+/g, "-").toLowerCase()}.ics`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        toast.error("Could not generate the calendar file.");
                      }
                    }}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-emerald-400 hover:text-emerald-200"
                  >
                    <FileDown className="h-3 w-3" /> Calendar
                  </button>
                  <button
                    data-testid={`trip-manifest-${t.id.slice(0, 6)}`}
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/owner/trips/${t.id}/manifest.pdf`, { headers, responseType: "blob" });
                        const url = URL.createObjectURL(res.data);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `manifest-${t.client.name.replace(/\s+/g, "-").toLowerCase()}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch {
                        toast.error("Could not build the manifest.");
                      }
                    }}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <FileDown className="h-3 w-3" /> Manifest
                  </button>
                  <button
                    data-testid={`trip-rooming-${t.id.slice(0, 6)}`}
                    onClick={() => {
                      setRoomingFor(roomingFor === t.id ? null : t.id);
                      setRoomingEmail("");
                    }}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <BedDouble className="h-3 w-3" /> Rooming
                  </button>
                  <button
                    data-testid={`trip-demo-email-${t.id.slice(0, 6)}`}
                    onClick={() => {
                      setDemoFor(demoFor === t.id ? null : t.id);
                      setDemoEmail("");
                    }}
                    className="flex items-center gap-1.5 border border-amber-500/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-amber-200/90 transition-colors hover:bg-amber-500 hover:text-black"
                  >
                    <Mail className="h-3 w-3" /> Email Demo
                  </button>
                  <button
                    data-testid={`trip-admins-${t.id.slice(0, 6)}`}
                    onClick={() => {
                      setAdminFor(adminFor === t.id ? null : t.id);
                      setAdminEmail("");
                    }}
                    className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                  >
                    <UserPlus className="h-3 w-3" /> Admins{(t.trip_admins || []).length > 0 ? ` (${t.trip_admins.length})` : ""}
                  </button>
                  {["draft", "changes_requested", "sent_to_client"].includes(t.status) && (
                    <button
                      data-testid={`trip-send-${t.id.slice(0, 6)}`}
                      onClick={() => sendToClient(t)}
                      disabled={busy === t.id}
                      className="flex items-center gap-1.5 border border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
                    >
                      {busy === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      {t.status === "draft" ? "Send to Client" : "Resend to Client"}
                    </button>
                  )}
                  {t.status === "pending_confirmation" && (
                    <button
                      data-testid={`trip-operator-sent-${t.id.slice(0, 6)}`}
                      onClick={() => lifecycle(t, "operator-sent", "sent_to_operator", "Marked as sent to operator")}
                      disabled={busy === t.id}
                      className="flex items-center gap-1.5 border border-violet-400/50 bg-violet-500/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-200 transition-colors hover:bg-violet-500 hover:text-black disabled:opacity-50"
                    >
                      <Send className="h-3 w-3" /> Request Sent to Operator
                    </button>
                  )}
                  {t.status === "sent_to_operator" && (
                    <button
                      data-testid={`trip-operator-confirmed-${t.id.slice(0, 6)}`}
                      onClick={() => lifecycle(t, "operator-confirmed", "confirmed", "Operator confirmed — itinerary is confirmed")}
                      disabled={busy === t.id}
                      className="flex items-center gap-1.5 border border-emerald-400/50 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Operator Confirmed
                    </button>
                  )}
                  {t.deposit_amount > 0 && t.deposit_status !== "paid" && (
                    <button
                      data-testid={`trip-deposit-paid-${t.id.slice(0, 6)}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/owner/trips/${t.id}/deposit-paid`, {}, { headers });
                          patchLocal(t.id, { deposit_status: "paid" });
                          toast.success("Deposit marked as paid");
                        } catch {
                          toast.error("Could not update the deposit.");
                        }
                      }}
                      className="flex items-center gap-1.5 border border-emerald-400/50 bg-emerald-500/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Deposit Paid
                    </button>
                  )}
                  {t.deposit_amount > 0 && t.deposit_status === "paid" && (
                    <button
                      data-testid={`trip-deposit-unpaid-${t.id.slice(0, 6)}`}
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/owner/trips/${t.id}/deposit-unpaid`, {}, { headers });
                          patchLocal(t.id, { deposit_status: "unpaid" });
                          toast.success("Deposit marked as unpaid");
                        } catch {
                          toast.error("Could not update the deposit.");
                        }
                      }}
                      className="flex items-center gap-1.5 border border-neutral-700 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-400 transition-colors hover:border-amber-400 hover:text-amber-200"
                    >
                      Undo Deposit
                    </button>
                  )}
                  <button
                    data-testid={`trip-delete-${t.id.slice(0, 6)}`}
                    onClick={() => remove(t)}
                    disabled={busy === t.id}
                    className="ml-auto flex items-center gap-1.5 border border-red-900/50 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300 disabled:opacity-50"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
                </>
                )}

                {adminFor === t.id && (
                  <div data-testid={`admins-panel-${t.id.slice(0, 6)}`} className="mt-4 border border-amber-500/25 bg-amber-500/5 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">
                      Trip admins — access to this journey only
                    </p>
                    {(t.trip_admins || []).length > 0 && (
                      <div className="mt-3 space-y-2">
                        {t.trip_admins.map((a) => (
                          <div key={a.token} data-testid={`admin-row-${a.token.slice(0, 6)}`} className="flex flex-wrap items-center gap-2 border border-neutral-800 bg-[#0c0c10] px-3 py-2">
                            <span className="flex-1 text-xs text-neutral-300">{a.email}</span>
                            <button
                              data-testid={`admin-copy-${a.token.slice(0, 6)}`}
                              onClick={() => {
                                navigator.clipboard.writeText(`${window.location.origin}/trip-admin/${a.token}`);
                                toast.success("Admin link copied");
                              }}
                              className="border border-neutral-700 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                            >
                              Copy link
                            </button>
                            <button
                              data-testid={`admin-remove-${a.token.slice(0, 6)}`}
                              onClick={async () => {
                                try {
                                  await axios.delete(`${API}/owner/trips/${t.id}/admins/${a.token}`, { headers });
                                  patchLocal(t.id, { trip_admins: (t.trip_admins || []).filter((x) => x.token !== a.token) });
                                  toast.success("Admin access revoked");
                                } catch {
                                  toast.error("Could not remove the admin.");
                                }
                              }}
                              className="border border-red-900/50 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-red-400/80 transition-colors hover:border-red-500 hover:text-red-300"
                            >
                              Revoke
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        data-testid={`admin-email-${t.id.slice(0, 6)}`}
                        type="email"
                        placeholder="Admin's email — they get a link to this trip only"
                        value={adminEmail}
                        onChange={(e) => setAdminEmail(e.target.value)}
                        className="min-w-56 flex-1 border border-amber-900/40 bg-[#0d0d12] px-3 py-2 text-sm text-amber-100 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                      />
                      <button
                        data-testid={`admin-invite-${t.id.slice(0, 6)}`}
                        onClick={async () => {
                          if (!adminEmail.trim()) {
                            toast.error("Add the admin's email first.");
                            return;
                          }
                          try {
                            const res = await axios.post(
                              `${API}/owner/trips/${t.id}/admins`,
                              { email: adminEmail },
                              { headers }
                            );
                            patchLocal(t.id, {
                              trip_admins: [...(t.trip_admins || []), { token: res.data.token, email: adminEmail }],
                            });
                            toast.success(res.data.email_id ? `Admin invited — link emailed to ${adminEmail}` : "Admin added — copy their link above");
                            setAdminEmail("");
                          } catch (e) {
                            const detail = e.response?.data?.detail;
                            toast.error(typeof detail === "string" ? detail : "Could not invite the admin.");
                          }
                        }}
                        className="border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                      >
                        Invite Admin
                      </button>
                    </div>
                  </div>
                )}

                {demoFor === t.id && (
                  <div data-testid={`demo-panel-${t.id.slice(0, 6)}`} className="mt-4 border border-amber-500/25 bg-amber-500/5 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">
                      Email yourself a demo — exactly what the client receives
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      The trip is NOT marked as sent and the client gets nothing. Leave blank to use your own concierge inbox.
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        data-testid={`demo-email-${t.id.slice(0, 6)}`}
                        type="email"
                        placeholder="Your email (blank = your concierge inbox)"
                        value={demoEmail}
                        onChange={(e) => setDemoEmail(e.target.value)}
                        className="min-w-56 flex-1 border border-amber-900/40 bg-[#0d0d12] px-3 py-2 text-sm text-amber-100 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                      />
                      <button
                        data-testid={`demo-send-${t.id.slice(0, 6)}`}
                        onClick={async () => {
                          try {
                            const res = await axios.post(
                              `${API}/owner/trips/${t.id}/send-demo`,
                              { to: demoEmail },
                              { headers }
                            );
                            toast.success(`Demo emailed to ${res.data.to}`);
                            setDemoFor(null);
                            setDemoEmail("");
                          } catch (e) {
                            const detail = e.response?.data?.detail;
                            toast.error(typeof detail === "string" ? detail : "Could not send the demo.");
                          }
                        }}
                        className="border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                      >
                        Send Demo
                      </button>
                    </div>
                  </div>
                )}

                {roomingFor === t.id && (
                  <div data-testid={`rooming-panel-${t.id.slice(0, 6)}`} className="mt-4 border border-amber-500/25 bg-amber-500/5 p-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">
                      Rooming list — {(t.hotels || []).length} {(t.hotels || []).length === 1 ? "stay" : "stays"}
                    </p>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <input
                        data-testid={`rooming-email-${t.id.slice(0, 6)}`}
                        type="email"
                        placeholder="Operator email, e.g. reservations@hotel.is"
                        value={roomingEmail}
                        onChange={(e) => setRoomingEmail(e.target.value)}
                        className="min-w-56 flex-1 border border-amber-900/40 bg-[#0d0d12] px-3 py-2 text-sm text-amber-100 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                      />
                      <button
                        data-testid={`rooming-send-${t.id.slice(0, 6)}`}
                        onClick={async () => {
                          if (!roomingEmail.trim()) {
                            toast.error("Add the operator's email first.");
                            return;
                          }
                          try {
                            const res = await axios.post(
                              `${API}/owner/trips/${t.id}/rooming-email`,
                              { to: roomingEmail },
                              { headers }
                            );
                            toast.success(res.data.email_id ? "Rooming list emailed" : "Could not send — check the address");
                            setRoomingFor(null);
                            setRoomingEmail("");
                          } catch {
                            toast.error("Could not send the rooming list.");
                          }
                        }}
                        className="border border-amber-500/50 px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black"
                      >
                        Email to Operator
                      </button>
                      <button
                        data-testid={`rooming-download-${t.id.slice(0, 6)}`}
                        onClick={async () => {
                          try {
                            const res = await axios.get(`${API}/owner/trips/${t.id}/rooming-pdf`, {
                              headers,
                              responseType: "blob",
                            });
                            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `rooming-list-${t.client.name.replace(/\s+/g, "-").toLowerCase()}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                          } catch {
                            toast.error("Could not generate the rooming list.");
                          }
                        }}
                        className="border border-neutral-700 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                      >
                        Download PDF
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          };

          // sections: worked on this week → arriving within 3 weeks → calendar → the rest
          const nowMs = Date.now();
          const todayIso = new Date().toISOString().slice(0, 10);
          const sourceTrips = visibleTrips || [];
          const recentTrips = sourceTrips
            .filter((t) => t.updated_at && nowMs - new Date(t.updated_at).getTime() < 7 * 86400000)
            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
          const recentIds = new Set(recentTrips.map((t) => t.id));
          const soonTrips = sourceTrips
            .filter((t) => {
              if (recentIds.has(t.id) || !t.arrival_date) return false;
              const inProgress = t.departure_date && t.arrival_date <= todayIso && t.departure_date >= todayIso;
              const days = Math.floor((new Date(`${t.arrival_date}T00:00:00`).getTime() - nowMs) / 86400000);
              return inProgress || (days >= 0 && days <= 21);
            })
            .sort((a, b) => a.arrival_date.localeCompare(b.arrival_date));
          const soonIds = new Set(soonTrips.map((t) => t.id));
          const restTrips = sourceTrips.filter((t) => !recentIds.has(t.id) && !soonIds.has(t.id));

          const SectionHead = ({ testid, title, sub }) => (
            <div className="mb-4 border-b border-neutral-800/70 pb-3">
              <p data-testid={testid} className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">{title}</p>
              {sub && <p className="mt-1 text-xs text-neutral-500">{sub}</p>}
            </div>
          );

          if (tripsTab === "former") {
            return (
              <section data-testid="trips-section-former">
                <SectionHead testid="trips-section-former-head" title="Former clients" sub="Finished journeys, fully settled. A client's next trip starts as a new itinerary." />
                {restTrips.length + recentTrips.length + soonTrips.length === 0 ? (
                  <p data-testid="former-empty" className="border border-dashed border-neutral-700 py-16 text-center text-sm text-neutral-500">
                    No former clients yet — finished trips land here once you settle the money question.
                  </p>
                ) : (
                  <div className="space-y-5">{sourceTrips.map(renderTripCard)}</div>
                )}
              </section>
            );
          }

          return (
            <>
              {recentTrips.length > 0 && (
                <section data-testid="trips-section-recent">
                  <SectionHead testid="trips-section-recent-head" title="Worked on this week" sub="Clients you touched in the last 7 days stay at the top." />
                  <div className="space-y-5">{recentTrips.map(renderTripCard)}</div>
                </section>
              )}
              {soonTrips.length > 0 && (
                <section data-testid="trips-section-soon">
                  <SectionHead testid="trips-section-soon-head" title="Arriving within 3 weeks" sub="Upcoming and in-progress journeys — soonest first." />
                  <div className="space-y-5">{soonTrips.map(renderTripCard)}</div>
                </section>
              )}
              <TripCalendar trips={visibleTrips} onOpen={openEditor} />
              {restTrips.length > 0 && (
                <section data-testid="trips-section-rest">
                  <SectionHead testid="trips-section-rest-head" title="All other clients" sub={`${restTrips.length} ${restTrips.length === 1 ? "itinerary" : "itineraries"} — newest work first`} />
                  <div className="space-y-5">{restTrips.map(renderTripCard)}</div>
                </section>
              )}
            </>
          );
          })())}
      </div>

      <AnimatePresence>
        {costsFor && (
          <motion.div
            data-testid="costs-modal"
            className="fixed inset-0 z-[95] overflow-y-auto bg-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="mx-auto max-w-lg px-4 py-8">
              <div className="border border-amber-500/20 bg-[#0c0c10]">
                <div className="flex items-center justify-between border-b border-amber-500/15 px-5 py-4">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Trip Costs — {costsFor.client?.name}</p>
                    <p className="mt-1 text-[11px] text-neutral-500">Only you see this — it never touches the client's bill.</p>
                  </div>
                  <button data-testid="costs-close-button" onClick={() => setCostsFor(null)} className="text-neutral-400 transition-colors hover:text-white" aria-label="Close costs">
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="border-b border-amber-500/10 p-5">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      data-testid="cost-amount-input"
                      placeholder="Amount — 25.000 or 180 EUR"
                      value={costAmount}
                      onChange={(e) => setCostAmount(e.target.value)}
                      onBlur={(e) => {
                        const v = evalMoneyExpr(e.target.value);
                        if (v) setCostAmount(v);
                      }}
                      className="min-w-36 flex-1 border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-50 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                    />
                    <label
                      data-testid="cost-photo-label"
                      className="flex cursor-pointer items-center gap-2 border border-neutral-700 px-3 py-2.5 text-[10px] uppercase tracking-[0.15em] text-neutral-300 transition-colors hover:border-amber-400 hover:text-amber-200"
                    >
                      <Plus className="h-3.5 w-3.5" /> Receipt photo
                      <input data-testid="cost-photo-input" ref={costPhotoRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" />
                    </label>
                  </div>
                  <input
                    data-testid="cost-note-input"
                    placeholder="Note — what is this? (e.g. snowmobile vendor invoice)"
                    value={costNote}
                    onChange={(e) => setCostNote(e.target.value)}
                    className="mt-2 w-full border border-amber-900/40 bg-[#0d0d12] px-3 py-2.5 text-sm text-amber-50 placeholder:text-neutral-600 outline-none focus:border-amber-400"
                  />
                  <button
                    data-testid="cost-add-button"
                    onClick={addCost}
                    disabled={costBusy}
                    className="mt-3 flex w-full items-center justify-center gap-2 bg-amber-500 px-4 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-50"
                  >
                    {costBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Log cost
                  </button>
                </div>

                {costs === null ? (
                  <div className="flex justify-center py-10">
                    <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                      <span>
                        Spent <span data-testid="costs-total" className="ml-1 text-amber-100">{fmtIsk(costs.total_isk)}</span>
                      </span>
                      <span>
                        Still to bill <span data-testid="costs-open-total" className="ml-1 text-amber-300">{fmtIsk(costs.open_isk)}</span>
                      </span>
                    </div>
                    <div className="divide-y divide-neutral-800/70">
                      {costs.costs.length === 0 && (
                        <p data-testid="costs-empty" className="px-5 py-8 text-center text-sm text-neutral-600">
                          Nothing logged yet — add the first cost above.
                        </p>
                      )}
                      {costs.costs.map((c) => (
                        <div key={c.cost_id} data-testid={`cost-row-${c.cost_id}`} className={`flex items-center gap-3 px-5 py-3 ${c.billed ? "opacity-50" : ""}`}>
                          {c.photo_path && (
                            <button
                              data-testid={`cost-photo-${c.cost_id}`}
                              onClick={() => photoUrls[c.cost_id] && setPhotoZoom(photoUrls[c.cost_id])}
                              className="h-14 w-14 shrink-0 overflow-hidden border border-neutral-700 transition-colors hover:border-amber-400"
                              aria-label="View receipt photo"
                            >
                              {photoUrls[c.cost_id] ? (
                                <img src={photoUrls[c.cost_id]} alt="Receipt" className="h-full w-full object-cover" />
                              ) : (
                                <Loader2 className="mx-auto h-4 w-4 animate-spin text-neutral-600" />
                              )}
                            </button>
                          )}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-neutral-100">
                              {c.amount_isk > 0 && <span className="mr-2 font-luxe tracking-wide text-amber-100">{fmtIsk(c.amount_isk)}</span>}
                              {c.amount_isk === 0 && c.amount && <span className="mr-2 text-amber-100">{c.amount}</span>}
                            </p>
                            {c.note && <p className="truncate text-xs text-neutral-400">{c.note}</p>}
                            <p className="mt-0.5 text-[9px] uppercase tracking-[0.18em] text-neutral-600">
                              {new Date(c.uploaded_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                              {" · "}
                              {new Date(c.uploaded_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                            </p>
                          </div>
                          <button
                            data-testid={`cost-billed-${c.cost_id}`}
                            onClick={() => toggleCostBilled(c)}
                            aria-pressed={c.billed}
                            title={c.billed ? "Billed — tap to reopen" : "Tap when this is billed to the client"}
                            className={`flex items-center gap-1.5 border px-2.5 py-1.5 text-[9px] uppercase tracking-[0.15em] transition-colors ${
                              c.billed
                                ? "border-emerald-400/50 text-emerald-200"
                                : "border-neutral-700 text-neutral-400 hover:border-amber-400 hover:text-amber-200"
                            }`}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> {c.billed ? "Billed" : "To bill"}
                          </button>
                          <button
                            data-testid={`cost-delete-${c.cost_id}`}
                            onClick={() => deleteCost(c)}
                            className="text-neutral-600 transition-colors hover:text-red-400"
                            aria-label="Delete cost"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {vatSummary && (
                  <div data-testid="vat-summary" className="border-t border-amber-500/10 px-5 py-4">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">VAT position — this journey</p>
                    <div className="mt-3 space-y-1.5 text-xs">
                      <p className="flex items-center justify-between gap-3">
                        <span className="text-neutral-400">VAT inside client prices (11%)</span>
                        <span data-testid="vat-output" className="font-luxe tracking-wide text-amber-100">{fmtIsk(vatSummary.output_vat_isk)}</span>
                      </p>
                      <p className="flex items-center justify-between gap-3">
                        <span className="text-neutral-400">VAT credit from vendor invoices</span>
                        <span data-testid="vat-credit" className="font-luxe tracking-wide text-emerald-200">− {fmtIsk(vatSummary.credit_isk)}</span>
                      </p>
                      <p className="flex items-center justify-between gap-3 border-t border-amber-500/10 pt-1.5">
                        <span className="text-neutral-200">Left to pay the tax office</span>
                        <span data-testid="vat-difference" className="font-luxe tracking-wide text-amber-200">{fmtIsk(vatSummary.difference_isk)}</span>
                      </p>
                    </div>
                    {vatSummary.missing.length > 0 && (
                      <p data-testid="vat-missing" className="mt-3 border border-red-500/40 bg-red-950/20 px-3 py-2 text-[10px] uppercase leading-relaxed tracking-[0.15em] text-red-200/90">
                        {vatSummary.missing.length} {vatSummary.missing.length === 1 ? "job needs" : "jobs need"} a vendor invoice:{" "}
                        {vatSummary.missing.map((m) => m.title).join(" · ")}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
        {photoZoom && (
          <motion.div
            data-testid="cost-photo-zoom"
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/95 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPhotoZoom(null)}
          >
            <img src={photoZoom} alt="Receipt" className="max-h-full max-w-full object-contain" />
            <button data-testid="cost-photo-zoom-close" onClick={() => setPhotoZoom(null)} className="absolute right-4 top-4 text-neutral-300 hover:text-white" aria-label="Close photo">
              <X className="h-6 w-6" />
            </button>
          </motion.div>
        )}
        {sheetTrip && (
          <AgentSheet
            key={sheetTrip.id}
            trip={sheetTrip}
            opsUrl={`${API}/owner/trips/${sheetTrip.id}/ops`}
            pdfUrl={`${API}/owner/trips/${sheetTrip.id}/agent-sheet.pdf`}
            headers={headers}
            invBase={`${API}/owner/trips/${sheetTrip.id}`}
            onClose={() => setSheetTrip(null)}
            onChange={(patch) => {
              patchLocal(sheetTrip.id, patch);
              setSheetTrip((t) => (t ? { ...t, ...patch } : t));
            }}
          />
        )}
        {previewTrip && (
          <motion.div
            data-testid="trip-demo-modal"
            className="fixed inset-0 z-[95] overflow-y-auto bg-black/85 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="mx-auto max-w-3xl px-5 py-10">
              <div className="mb-4 flex items-center justify-between border border-amber-500/40 bg-amber-500/10 px-5 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-200">
                  Demo — exactly what your client sees · bin a moment or click a title/time to edit (saves instantly)
                </p>
                <button data-testid="demo-close-button" onClick={() => setPreviewTrip(null)} className="text-amber-200 transition-colors hover:text-white" aria-label="Close demo">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="border border-amber-500/20 bg-[#070709] p-6 sm:p-10">
                <TripView
                  trip={previewTrip}
                  showClientMarks
                  showGapWarnings
                  onDelete={async (id) => {
                    const items = previewTrip.items.filter((x) => x.item_id !== id);
                    try {
                      await savePreviewItems(items);
                      toast.success("Moment removed — saved");
                    } catch {
                      toast.error("Could not remove the moment.");
                    }
                  }}
                  onEdit={async (id, field, value) => {
                    const items = previewTrip.items.map((x) => (x.item_id === id ? { ...x, [field]: value } : x));
                    try {
                      await savePreviewItems(items);
                      toast.success("Updated — saved");
                    } catch {
                      toast.error("Could not save the change.");
                    }
                  }}
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* client wishes — tap the wish badge on a card to read and resolve */}
      {wishesFor && (
        <div data-testid="wishes-modal" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setWishesFor(null)}>
          <div className="w-full max-w-lg border border-amber-500/25 bg-[#0c0c10] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Client wishes — {wishesFor.client?.name}</p>
              <button data-testid="wishes-close" onClick={() => setWishesFor(null)} className="text-neutral-500 transition-colors hover:text-amber-200" aria-label="Close wishes">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-4 space-y-3">
              {(wishesFor.client_wishes || []).map((w, wi) => (
                <div key={wi} data-testid={`wish-row-${wi}`} className={`border p-3 ${w.handled ? "border-neutral-800 opacity-50" : "border-amber-500/25 bg-amber-500/[0.04]"}`}>
                  <p className="text-sm leading-relaxed text-neutral-200">{w.text}</p>
                  <p className="mt-1 text-[9px] uppercase tracking-[0.2em] text-neutral-500">
                    {new Date(w.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  {w.reply && <p className="mt-2 border-l-2 border-amber-500/40 pl-2 text-xs italic text-neutral-400">You replied: {w.reply}</p>}
                  {w.handled ? (
                    <p data-testid={`wish-done-${wi}`} className="mt-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-300">
                      <Check className="h-3 w-3" /> Fixed
                    </p>
                  ) : (
                    <button
                      data-testid={`wish-fixed-${wi}`}
                      onClick={() => markWishFixed(wishesFor, wi)}
                      className="mt-2 flex items-center gap-1.5 border border-emerald-500/50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-emerald-200 transition-colors hover:bg-emerald-500 hover:text-black"
                    >
                      <Check className="h-3 w-3" /> Mark as fixed
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* fresh client requests — popup after sign-in; one tap opens the builder pre-filled */}
      {newRequests && newRequests.length > 0 && (
        <div data-testid="new-requests-popup" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setNewRequests(null)}>
          <div className="w-full max-w-lg border border-amber-500/25 bg-[#0c0c10] p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">
                {newRequests.length} new request{newRequests.length === 1 ? "" : "s"} waiting
              </p>
              <button data-testid="new-requests-close" onClick={() => setNewRequests(null)} className="text-neutral-500 transition-colors hover:text-amber-200" aria-label="Close requests">
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-xs text-neutral-500">Tap one to start building that itinerary straight away.</p>
            <div className="mt-4 space-y-2">
              {newRequests.map((q) => (
                <button
                  key={q.id}
                  data-testid={`new-request-${String(q.id).slice(0, 6)}`}
                  onClick={() => buildFromRequest(q)}
                  className="flex w-full items-center justify-between gap-3 border border-neutral-800 bg-black/20 px-4 py-3 text-left transition-colors hover:border-amber-400 hover:bg-amber-500/5"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-serif text-lg font-light text-neutral-50">{q.name}</span>
                    <span className="mt-0.5 block truncate text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                      {q.arrival_date || "?"} → {q.departure_date || "?"}{q.party_size ? ` · ${q.party_size} pax` : ""}{(q.services || []).length ? ` · ${q.services.slice(0, 3).join(", ")}` : ""}
                    </span>
                  </span>
                  <span className="shrink-0 border border-amber-500/50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200">Build →</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
